import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Upload, X, Send, FileText, Image as ImageIcon, Film, Music,
  AlertTriangle, Mic,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "text-slate-600 border-slate-300" },
  { value: "medium", label: "Medium", color: "text-cyan-700 border-cyan-300" },
  { value: "high", label: "High", color: "text-orange-700 border-orange-300" },
  { value: "urgent", label: "Urgent", color: "text-red-700 border-red-300" },
];

function fileIconFor(name = "") {
  const ext = name.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "heic", "gif"].includes(ext)) return ImageIcon;
  if (["mp4", "mov", "webm", "avi"].includes(ext)) return Film;
  if (["mp3", "m4a", "wav", "ogg", "aac"].includes(ext)) return Music;
  return FileText;
}

export default function DelegationCreate() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    category: "",
    target_finish_date: "",
    is_confidential: false,
    on_behalf_of: "",
  });
  const [attachments, setAttachments] = useState([]); // {id, file_name, file_size, file_type}
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState(null);
  const [seniorMgmtList, setSeniorMgmtList] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
        if (data?.role === "ea") {
          const { data: principals } = await api.get("/delegation/my-principals");
          setSeniorMgmtList(principals || []);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Debounced employee search
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/delegation/pickable-employees", {
          params: { q: search, limit: 25 },
        });
        setEmployees(data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const onUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (attachments.length + fileList.length > 10) {
      toast.error("Max 10 files");
      return;
    }
    for (const f of fileList) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 25 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/delegation/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
        toast.success(`Attached: ${f.name}`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const onSubmit = async () => {
    if (!selectedEmp) return toast.error("Please select an employee");
    if (form.title.trim().length < 3) return toast.error("Title must be at least 3 characters");
    if (!form.target_finish_date) return toast.error("Finish date is required");
    if (me?.role === "ea" && !form.on_behalf_of)
      return toast.error("EA must select Senior Mgmt principal");
    setSubmitting(true);
    try {
      const { data } = await api.post("/delegation/tasks", {
        ...form,
        title: form.title.trim(),
        description: form.description.trim() || null,
        assigned_to: selectedEmp.id,
        attachment_ids: attachments.map((a) => a.id),
        on_behalf_of: form.on_behalf_of || null,
        voice_url: voiceUrl || null,
        voice_duration: voiceUrl ? voiceDuration : null,
      });
      toast.success(`Task created: ${data.task_number}`);
      navigate(`/delegation/tasks/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const totalAttSize = useMemo(
    () => attachments.reduce((a, x) => a + (x.file_size || 0), 0),
    [attachments]
  );

  return (
    <div className="space-y-5" data-testid="delegation-create">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Delegation</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Create New Task
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Assign work to an employee with text description + attachments. Notifications go out
          immediately. CC: their Manager + HR.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — task fields */}
        <div className="lg:col-span-2 space-y-4 bg-white rounded-xl border border-red-100 p-5">
          {/* Employee picker */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Assign To <span className="text-red-500">*</span>
            </Label>
            {selectedEmp ? (
              <div className="mt-1.5 flex items-center justify-between gap-2 bg-red-50/40 border border-red-200 rounded p-2.5"
                   data-testid="selected-employee">
                <div>
                  <div className="font-semibold text-slate-900">{selectedEmp.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {selectedEmp.punch_id} · {selectedEmp.designation || selectedEmp.role}
                  </div>
                </div>
                <button onClick={() => setSelectedEmp(null)} className="text-red-700 hover:underline text-xs">
                  <X className="h-3.5 w-3.5 inline" /> Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative mt-1.5">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, punch ID, or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="employee-search"
                  />
                </div>
                {employees.length > 0 && (
                  <div className="mt-1 max-h-48 overflow-y-auto border border-slate-200 rounded bg-white"
                       data-testid="employee-list">
                    {employees.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => { setSelectedEmp(e); setSearch(""); }}
                        className="w-full text-left px-3 py-2 hover:bg-red-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="text-sm font-medium text-slate-900">{e.name}</div>
                        <div className="text-[11px] text-slate-500">
                          {e.punch_id} · {e.designation || e.role || "—"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* EA on-behalf-of */}
          {me?.role === "ea" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                On Behalf Of (Senior Mgmt) <span className="text-red-500">*</span>
              </Label>
              {seniorMgmtList.length === 0 ? (
                <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-800" data-testid="ea-no-principals">
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                  You are not yet mapped to any Senior Mgmt principal. Please contact HR to assign one before creating tasks.
                </div>
              ) : (
                <Select value={form.on_behalf_of} onValueChange={(v) => setForm({ ...form, on_behalf_of: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="on-behalf-select">
                    <SelectValue placeholder="Select Senior Mgmt..." />
                  </SelectTrigger>
                  <SelectContent>
                    {seniorMgmtList.map((sm) => (
                      <SelectItem key={sm.id} value={sm.id}>{sm.name} ({sm.punch_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Task Title <span className="text-red-500">*</span>
            </Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Prepare Q1 Sales Report"
              maxLength={200}
              className="mt-1.5"
              data-testid="title-input"
            />
          </div>

          {/* Priority + Category + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                Priority <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-1 mt-1.5" data-testid="priority-options">
                {PRIORITY_OPTIONS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setForm({ ...form, priority: p.value })}
                    className={`px-2 py-1.5 text-xs font-medium rounded border ${p.color} ${
                      form.priority === p.value ? "ring-2 ring-red-300 bg-red-50/30" : "bg-white"
                    }`}
                    data-testid={`priority-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Category</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Sales"
                className="mt-1.5"
                data-testid="category-input"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                Finish By <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={form.target_finish_date}
                onChange={(e) => setForm({ ...form, target_finish_date: e.target.value })}
                min={new Date().toISOString().slice(0, 10)}
                className="mt-1.5"
                data-testid="finish-date-input"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Description</Label>
            <Textarea
              rows={6}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the task in detail — context, expected output, key points to cover..."
              className="mt-1.5"
              data-testid="description-input"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              {form.description.length} chars · Aim for ≥30 chars for clarity.
            </div>
          </div>

          {/* Voice Note */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Mic className="h-3 w-3" /> Voice Instruction (optional · max 60s)
            </Label>
            <div className="mt-1.5">
              <VoiceRecorder
                value={voiceUrl}
                onChange={(url, dur) => { setVoiceUrl(url); setVoiceDuration(dur || 0); }}
                uploadEndpoint="/delegation/attachments"
                uploadFields={{}}
                urlFromResponse={(d) => d.file_url}
                testIdPrefix="task-voice"
              />
            </div>
          </div>

          {/* Attachments */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Attachments ({attachments.length}/10)
            </Label>
            <label className="mt-1.5 flex items-center justify-center gap-2 border-2 border-dashed border-red-200 rounded p-4 text-xs text-slate-600 cursor-pointer hover:bg-red-50/30">
              <Upload className="h-4 w-4 text-red-500" />
              <span>Drag &amp; drop or click to browse · PDF / DOC / XLS / PPT / Images / Audio / Video</span>
              <input
                type="file"
                multiple
                onChange={(e) => onUpload(e.target.files)}
                className="hidden"
                data-testid="attachments-input"
              />
            </label>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="attachments-list">
                {attachments.map((a) => {
                  const Icon = fileIconFor(a.file_name);
                  return (
                    <div key={a.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs">
                      <Icon className="h-4 w-4 text-slate-500" />
                      <span className="flex-1 truncate">{a.file_name}</span>
                      <span className="text-slate-400">{(a.file_size / 1024 / 1024).toFixed(2)} MB</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.id)}
                        className="text-red-600 hover:underline"
                        data-testid={`remove-att-${a.id}`}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
                <div className="text-[10px] text-slate-400 text-right">
                  Total: {(totalAttSize / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right — summary + submit */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-red-100 p-5 space-y-3">
            <div className="text-sm font-semibold text-slate-900">Summary</div>
            <Row label="Assignee" value={selectedEmp ? `${selectedEmp.name} (${selectedEmp.punch_id})` : "—"} />
            <Row label="Priority" value={form.priority.toUpperCase()} />
            <Row label="Finish" value={form.target_finish_date || "—"} />
            <Row label="Description" value={form.description ? `${form.description.length} chars` : "—"} />
            <Row label="Attachments" value={`${attachments.length} files (${(totalAttSize / 1024 / 1024).toFixed(2)} MB)`} />
            <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
              Email to assignee + CC their Manager + HR sent on submit.
            </div>
          </div>
          {form.priority === "urgent" && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                Urgent tasks should be reserved for genuine time-critical work. Employee is expected to acknowledge within hours.
              </span>
            </div>
          )}
          <Button
            onClick={onSubmit}
            disabled={submitting || !selectedEmp || form.title.trim().length < 3 || !form.target_finish_date}
            className="w-full brand-bg brand-bg-hover text-white"
            data-testid="submit-task-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Creating..." : "Create & Send Task"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium text-right truncate max-w-[180px]">{value}</span>
    </div>
  );
}
